import { Estimate, EstimateStatus } from '@talon/web/lib/api';
import { useEstimateStore } from '@talon/web/src/context';
import { USD } from '@talon/web/src/utils/currency';
import { Box, Button, Card, CheckBox, Heading, Text } from 'grommet';
import { CircleInformation, Refresh, ShareOption } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { env } from '../../../config/env';
import { ConfirmModal } from '../ConfirmModal';
import { EditableText } from '../EditableText';
import { SaveStatus, SaveStatusIndicator } from '../SaveStatusIndicator';
import { TalonTip } from '../TalonTip';

export interface IEstimateHeaderProps {
  estimate: Estimate;
  updateEstimate: (estimate: Estimate) => void;
}

export const EstimateHeader = observer(() => {
  /** Context **/
  const estimateStore = useEstimateStore();
  const location = useLocation();

  /** State **/
  const [showRerunModal, setShowRerunModal] = useState(false);

  /** Computed **/
  const saveStatus = estimateStore.isSaving ? SaveStatus.Saving : SaveStatus.Saved;

  /** Methods **/

  const updateEstimate = async (updatedEstimate: Estimate) => {
    await estimateStore.updateEstimate(updatedEstimate);
    const estimate = await estimateStore.getEstimate(updatedEstimate.id, 'session_estimates,usage_estimates');
    if (estimate) estimateStore.setCurrentEstimate(estimate);
  };

  const copyShareLink = async () => {
    const link = new URL(location.pathname, env.clientUrl).href;
    await navigator.clipboard.writeText(link);
    toast.success('Copied link to clipboard.');
  };

  const recalculateEstimate = async () => {
    if (!estimateStore.currentEstimate) return;
    await estimateStore.recalculateEstimate(estimateStore.currentEstimate);
    setShowRerunModal(false);
  };

  /** Render **/
  const StatusToggle = ({ width }: { width: string }) => (
    <Box width={width} flex="grow" direction="row" gap="small" pad="small" align="center" justify="end">
      <Button
        onClick={() => copyShareLink()}
        icon={<ShareOption color="plain" />}
        hoverIndicator
        tip="Share this estimate"
      />
      <Heading level="6" weight="normal" fill={false}>
        STATUS&nbsp;
        <TalonTip
          content={
            <Text>
              Draft estimates are &quot;ballpark&quot; estimates. Final estimates have been approved by the client.
            </Text>
          }
        >
          <CircleInformation size="small" />
        </TalonTip>
      </Heading>
      <CheckBox
        // pad="medium"
        label={
          <Box justify="center" width="xxsmall">
            <Text alignSelf="center" size="small">
              {estimateStore.currentEstimate?.status?.toUpperCase()}
            </Text>
          </Box>
        }
        checked={estimateStore.currentEstimate?.status === EstimateStatus.Final}
        onChange={(event) =>
          estimateStore.currentEstimate?.id &&
          updateEstimate({
            ...estimateStore.currentEstimate,
            status: event.target.checked ? EstimateStatus.Final : EstimateStatus.Draft,
          })
        }
        toggle
      />
    </Box>
  );

  return (
    <Card pad="medium" background="light-2" margin="medium" round="medium" overflow="auto">
      <Box direction="column">
        {/* Estimate Title */}
        <Box direction="row" justify="center" margin={{ horizontal: 'small' }}>
          <Box flex />
          <Box flex>
            <Heading level="3" alignSelf="center">
              <EditableText
                val={estimateStore.currentEstimate?.title || ''}
                onChange={(t) =>
                  estimateStore.currentEstimate?.id && updateEstimate({ ...estimateStore.currentEstimate, title: t })
                }
                textSize="xlarge"
              />
            </Heading>
          </Box>
          <Box justify="center" align="end" flex>
            <SaveStatusIndicator saveStatus={saveStatus} boxProps={{ margin: 'none' }} />
          </Box>
        </Box>
        <Box direction="row" justify="between" height="xxsmall">
          <Box width="25%" align="start" justify="center">
            <Button
              secondary
              size="small"
              label={
                <Text size="small" weight="bold">
                  RECALCULATE
                </Text>
              }
              icon={<Refresh size="16px" />}
              tip="Re-run estimate"
              onClick={() => setShowRerunModal(true)}
            />
          </Box>
          <Box width="50%" flex="grow" direction="row" gap="large" align="center">
            <Box direction="row" gap="xsmall" align="end" fill="horizontal">
              <Text size="small">TOTAL WAGES:</Text>
              <Text weight="bold">{USD(estimateStore.currentEstimate?.total)}</Text>
            </Box>
            <Box direction="row" gap="xsmall" align="end" fill="horizontal">
              <Text size="small">GRAND TOTAL:</Text>
              <Text weight="bold">{USD(estimateStore.currentEstimate?.total_plus_fringe)}</Text>
            </Box>
          </Box>
          <StatusToggle width="25%" />
        </Box>
      </Box>
      {showRerunModal && (
        <ConfirmModal
          content="NOTE: This will overwrite any edits made to this Estimate."
          confirmLabel="OK"
          onConfirm={() => recalculateEstimate()}
          onCancel={() => setShowRerunModal(false)}
        />
      )}
    </Card>
  );
});
